import React, { useCallback, useMemo, useState } from "react";
import { cx, isValidHttpUrl, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import {
  Button,
  LoadingSpinner,
  Alert,
  TextInput,
  Tab,
} from "@jugl-web/ui-kit";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { AgentKnowledgeContentContainer } from "../AgentKnowledgeContentContainer";
import { ReactComponent as WebsiteIcon } from "./assets/website.svg";
import { ReactComponent as WebIcon } from "./assets/web.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as HyperlinkIcon } from "./assets/hyperlink.svg";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";
import { LinkKnowledgePreview } from "./components/LinkKnowledgePreview";
import { LinkKnowledgeItem } from "./components/LinkKnowledgeItem";
import { LinkToPreview } from "./types";

type LinkKnowledgeTab = "crawl-links" | "individual-link";

export const AgentKnowledgeWebsiteContent: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { t } = useTranslations();
  const { aiWorkflowsApi } = useRestApiProvider();
  const {
    linksKnowledge,
    isLinksKnowledgeFetching,
    handleDeleteKnowledge,
    handleCXApiError,
  } = useAgentKnowledge(entityId);
  const { isAnonymous } = useAnonymousSession();
  const { isSubscriptionCanceled } = useSubscriptionV2();

  const isDisabled = isAnonymous && !!linksKnowledge?.length;

  const deleteConfirmationAlertState = useConfirmationDialogState();
  const excludeConfirmationAlertState = useConfirmationDialogState();
  const [link, setLink] = useState("");
  const [linkToPreview, setLinkToPreview] = useState<LinkToPreview>();
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [isAddingItemLoading, setIsAddingItemLoading] = useState(false);
  const [selectedTab, setSelectedTab] =
    useState<LinkKnowledgeTab>("crawl-links");
  const [crawlingId, setCrawlingId] = useState<string>();

  const tabs = useMemo(
    (): { id: LinkKnowledgeTab; title: string; icon: React.ReactNode }[] => [
      {
        id: "crawl-links",
        title: t({
          id: "cx-ai-agents-page.crawl-links",
          defaultMessage: "Crawl links",
        }),
        icon: <WebIcon />,
      },
      {
        id: "individual-link",
        title: t({
          id: "cx-ai-agents-page.individual-link",
          defaultMessage: "Individual link",
        }),
        icon: <HyperlinkIcon />,
      },
    ],
    [t]
  );

  const [getListLinks] = aiWorkflowsApi.useLazyGetListLinksQuery();
  const [uploadLinkKnowledge] = aiWorkflowsApi.useUploadLinkKnowledgeMutation();
  const [deleteParticularLink] =
    aiWorkflowsApi.useDeleteParticularLinkMutation();

  const handleDeleteParticularLink = useCallback(
    async (id: string, url: string) => {
      await deleteParticularLink({
        agentId: entityId,
        entityId,
        source_id: id,
        url,
        meta: { onError: handleCXApiError },
      });
    },
    [deleteParticularLink, entityId, handleCXApiError]
  );

  const handleCreateLinkKnowledge = useCallback(async () => {
    setIsAddingItemLoading(true);
    const linkWithProtocol = /^https?:\/\//i.test(link)
      ? link
      : `https://${link}`;

    if (selectedTab === "individual-link") {
      const result = await uploadLinkKnowledge({
        agentId: entityId,
        entityId,
        url: linkWithProtocol,
        links: [linkWithProtocol],
        meta: { onError: handleCXApiError },
      });
      if ("data" in result) {
        setLink("");
      }
    } else {
      const listLinks = await getListLinks({
        agentId: entityId,
        entityId,
        params: {
          url: linkWithProtocol,
        },
        meta: { onError: handleCXApiError },
      });
      if (listLinks.isSuccess && listLinks.data) {
        const result = await uploadLinkKnowledge({
          agentId: entityId,
          entityId,
          url: listLinks.data.links.url,
          links: listLinks.data.links.links,
          meta: { onError: handleCXApiError },
        });
        if ("data" in result) {
          setLink("");
        }
      }
    }

    setIsAddingItemLoading(false);
  }, [
    link,
    selectedTab,
    uploadLinkKnowledge,
    entityId,
    getListLinks,
    handleCXApiError,
  ]);

  const handleFetchLinks = useCallback(() => {
    const isInvalid = !isValidHttpUrl(link, false);
    setIsLinkInvalid(isInvalid);

    if (isInvalid) return;

    handleCreateLinkKnowledge();
  }, [handleCreateLinkKnowledge, link]);

  return (
    <>
      {linkToPreview ? (
        <LinkKnowledgePreview
          entityId={entityId}
          url={linkToPreview.url}
          sourceId={linkToPreview.id}
          onRequestClose={() => setLinkToPreview(undefined)}
          onDelete={() => {
            if (linkToPreview.type === "knowledge") {
              deleteConfirmationAlertState.open({
                onConfirm: () => {
                  handleDeleteKnowledge(linkToPreview.id, "links");
                  setLinkToPreview(undefined);
                },
              });
            } else if (linkToPreview.type === "link") {
              excludeConfirmationAlertState.open({
                onConfirm: () => {
                  handleDeleteParticularLink(
                    linkToPreview.id,
                    linkToPreview.url
                  );
                  setLinkToPreview(undefined);
                },
              });
            }
          }}
        />
      ) : (
        <AgentKnowledgeContentContainer
          icon={<WebsiteIcon />}
          title={t({
            id: "cx-ai-agents-page.website",
            defaultMessage: "Website",
          })}
          description={t({
            id: "cx-ai-agents-page.add-urls-to-train-your-ai-agent",
            defaultMessage: "Add URLs to your website to train your AI Agent",
          })}
        >
          <div className="flex w-max gap-1">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                title={
                  <div className="flex items-center gap-1.5">
                    {tab.icon}
                    <span>{tab.title}</span>
                  </div>
                }
                isSelected={tab.id === selectedTab}
                onClick={() => setSelectedTab(tab.id)}
                labelClassName="text-sm"
                shouldScrollIntoViewOnSelect={false}
                isDisabled={isAddingItemLoading}
              />
            ))}
          </div>
          <TextInput
            labelPosition="border-overlap"
            placeholder={
              selectedTab === "crawl-links"
                ? "mybusiness.com"
                : t({
                    id: "cx-ai-agents-page.insert-website-link",
                    defaultMessage: "Insert Website link",
                  })
            }
            label={t({
              id: "cx-ai-agents-page.website-link",
              defaultMessage: "Website link",
            })}
            isDisabled={
              isAddingItemLoading || isDisabled || isSubscriptionCanceled
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                handleFetchLinks();
              }
            }}
            endContent={
              isDisabled ? (
                <div className="flex items-center gap-2">
                  <span className="font-secondary text-sm text-dark">
                    {t({
                      id: "cx-ai-agents-page.signup-for-unlimited-urls",
                      defaultMessage: "Signup to get unlimited URL’s upload",
                    })}
                  </span>
                  <PricingIcon />
                </div>
              ) : link && isAddingItemLoading ? (
                <Button
                  variant="secondary"
                  isDisabled
                  onClick={handleFetchLinks}
                  size="sm"
                >
                  <LoadingSpinner size="xs" color="grey" />
                </Button>
              ) : undefined
            }
            classNames={{
              wrapperClassName: "shrink-0",
              endContentWrapperClassName: cx(
                "pl-0",
                isAddingItemLoading || isDisabled || isSubscriptionCanceled
                  ? "bg-[#FAFAFA]"
                  : "bg-white"
              ),
            }}
            value={link}
            onChange={(e) => setLink(e.target.value.trim())}
            isInvalid={isLinkInvalid}
            errorMessage={
              isLinkInvalid
                ? t({
                    id: "validation.invalid-url",
                    defaultMessage: "Enter a valid URL address",
                  })
                : undefined
            }
          />
          {link && !isSubscriptionCanceled && !isAddingItemLoading && (
            <Button
              className="w-[240px]"
              iconEnd={<ArrowIcon />}
              onClick={handleFetchLinks}
            >
              {t({
                id: "cx-ai-agents-page.fetch-link",
                defaultMessage: "Fetch Link",
              })}
            </Button>
          )}
          {!!linksKnowledge?.length && (
            <div className="mt-8 flex flex-col gap-4">
              <span className="font-secondary font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.addes-sources",
                  defaultMessage: "Added sources",
                })}
              </span>
              <div className="flex flex-col gap-6">
                {linksKnowledge?.map((item) => (
                  <LinkKnowledgeItem
                    key={item.data.id}
                    id={item.data.id}
                    sourceLink={item.data.ref}
                    onCrawlingChange={setCrawlingId}
                    isCrawling={crawlingId === item.data.id}
                    isDisabled={isLinksKnowledgeFetching || !!crawlingId}
                    entityId={entityId}
                    links={item.data.source}
                    onOpenPreview={setLinkToPreview}
                    onDelete={() =>
                      deleteConfirmationAlertState.open({
                        onConfirm: () =>
                          handleDeleteKnowledge(item.data.id, "links"),
                      })
                    }
                    onDeleteParticularLink={(url) =>
                      excludeConfirmationAlertState.open({
                        onConfirm: () =>
                          handleDeleteParticularLink(item.data.id, url),
                      })
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </AgentKnowledgeContentContainer>
      )}
      <Alert
        isOpen={deleteConfirmationAlertState.isOpen}
        onRequestClose={deleteConfirmationAlertState.close}
        title={t({
          id: "cx-ai-agents-page.delete-url",
          defaultMessage: "Delete URL",
        })}
        content={t({
          id: "cx-ai-agents-page.delete-url-knowledge-description-no-longer-trained",
          defaultMessage:
            "If you will delete selected URL, AI Agent will no longer be trained on its info",
        })}
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.delete", defaultMessage: "Delete" }),
            color: "tertiary",
            onClick: () => {
              deleteConfirmationAlertState.confirm({
                closeOnceConfirmed: true,
              });
            },
          },
        ]}
      />
      <Alert
        isOpen={excludeConfirmationAlertState.isOpen}
        onRequestClose={excludeConfirmationAlertState.close}
        title={t({
          id: "cx-ai-agents-page.exclude-url",
          defaultMessage: "Exclude URL",
        })}
        content={t({
          id: "cx-ai-agents-page.exclude-url-knowledge-description-no-longer-trained",
          defaultMessage:
            "If you will exclude the link, AI Agent will no longer be trained on the link info",
        })}
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.exclude", defaultMessage: "Exclude" }),
            color: "tertiary",
            onClick: () => {
              excludeConfirmationAlertState.confirm({
                closeOnceConfirmed: true,
              });
            },
          },
        ]}
      />
    </>
  );
};
